// Сервис для работы с авторизацией
export const authService = {
  // Декодировать JWT токен
  decodeJWT(token) {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null
      
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const padding = '='.repeat((4 - base64.length % 4) % 4)
      const decoded = atob(base64 + padding)
      const payload = JSON.parse(decoded)
      
      return payload
    } catch (e) {
      console.error('Error decoding JWT:', e)
      return null
    }
  },

  // Получить expiresAt из JWT токена
  getExpiresFromToken(token) {
    const payload = this.decodeJWT(token)
    return payload?.exp || null
  },

  // Получить полные данные авторизации
  getAuthData() {
    try {
      const authDataStr = localStorage.getItem('admin_auth_data')
      if (!authDataStr) {
        // Обратная совместимость со старым форматом
        const oldToken = localStorage.getItem('admin_token')
        if (oldToken) {
          return {
            token: oldToken,
            expiresAt: this.getExpiresFromToken(oldToken),
            refreshToken: null,
            user: null,
            lastRefreshed: null
          }
        }
        return null
      }
      return JSON.parse(authDataStr)
    } catch (e) {
      console.error('Error getting auth data:', e)
      return null
    }
  },

  // Сохранить полные данные авторизации
  setAuthData(token, refreshToken, user) {
    const authData = {
      token,
      refreshToken,
      user,
      expiresAt: this.getExpiresFromToken(token),
      lastRefreshed: Date.now()
    }
    
    localStorage.setItem('admin_auth_data', JSON.stringify(authData))
    // Также сохраняем отдельно для обратной совместимости
    localStorage.setItem('admin_token', token)
  },

  // Получить токен из localStorage (с проверкой истечения)
  async getToken() {
    const authData = this.getAuthData()
    if (!authData || !authData.token) return null
    
    // Проверяем истечение токена
    const now = Math.floor(Date.now() / 1000)
    
    if (authData.expiresAt) {
      const timeUntilExpiry = authData.expiresAt - now
      
      // Если токен истекает менее чем через 1 день (86400 секунд) или истек
      if (timeUntilExpiry < 86400) {
        console.log('[Auth] JWT токен истекает скоро или истек, пробуем обновить...')
        
        // Пытаемся обновить токен через refresh token
        if (authData.refreshToken) {
          const refreshResult = await this.refreshToken(authData.refreshToken)
          
          if (refreshResult.success) {
            console.log('[Auth] JWT токен успешно обновлен')
            return refreshResult.token
          } else {
            console.warn('[Auth] Не удалось обновить токен, требуется повторная авторизация.')
            this.logout()
            return null
          }
        } else {
          console.warn('[Auth] Нет refresh token для обновления.')
          if (timeUntilExpiry < 0) {
            // Токен уже истек
            this.logout()
            return null
          }
        }
      }
    }
    
    return authData.token
  },

  // Обновить access token через refresh token
  async refreshToken(refreshToken) {
    try {
      console.log('[Auth] Attempting to refresh access token...')
      
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      })
      
      const data = await response.json()
      
      if (!response.ok || !data.success) {
        console.error('[Auth] Failed to refresh token:', data.error)
        return { success: false, error: data.error }
      }
      
      // Сохраняем новые токены
      const authData = this.getAuthData()
      this.setAuthData(data.token, data.refresh_token, authData?.user)
      
      console.log('[Auth] Successfully refreshed access token')
      return {
        success: true,
        token: data.token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in
      }
      
    } catch (error) {
      console.error('[Auth] Error refreshing access token:', error)
      return { success: false, error: 'Ошибка подключения к серверу' }
    }
  },

  // Сохранить токен (устаревший метод для обратной совместимости)
  setToken(token) {
    localStorage.setItem('admin_token', token)
  },

  // Удалить токен (logout)
  logout() {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_auth_data')
  },

  // Получить информацию о времени до истечения токена
  getTokenTimeInfo() {
    const authData = this.getAuthData()
    if (!authData || !authData.expiresAt) return null
    
    const now = Math.floor(Date.now() / 1000)
    const expiresAt = authData.expiresAt
    const timeRemaining = expiresAt - now
    
    if (timeRemaining <= 0) {
      return {
        expired: true,
        timeRemaining: 0,
        percentRemaining: 0,
        lastRefreshed: authData.lastRefreshed,
        expiresAt: expiresAt * 1000
      }
    }
    
    const TOKEN_LIFETIME = 7 * 24 * 60 * 60 // 7 дней в секундах
    const percentRemaining = (timeRemaining / TOKEN_LIFETIME) * 100
    
    return {
      expired: false,
      timeRemaining,
      percentRemaining,
      lastRefreshed: authData.lastRefreshed,
      expiresAt: expiresAt * 1000
    }
  },

  // Google OAuth авторизация
  async authenticateWithGoogle(googleToken) {
    try {
      const response = await fetch('/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'google',
          token: googleToken
        })
      })

      const data = await response.json()
      
      if (data.success) {
        // Сохраняем полные данные авторизации (token + refresh_token)
        this.setAuthData(data.token, data.refresh_token, data.user)
        return { success: true, user: data.user }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      console.error('Auth error:', error)
      return { success: false, error: 'Ошибка подключения к серверу' }
    }
  }
}

